import React, { useState } from "react";
import { Link } from "react-router-dom";
import { FiInstagram, FiTwitter, FiFacebook, FiYoutube, FiSend, FiMapPin, FiMail, FiArrowRight } from "react-icons/fi";
import toast from "react-hot-toast";

/* Premium SVG icons for each social */
const SocialBtn = ({ Icon, href }) => (
  <a href={href} rel="noreferrer" target="_blank"
    className="w-9 h-9 rounded-xl border border-white/10 flex items-center justify-center text-gray-400 hover:text-white hover:border-brand-500 hover:bg-brand-600/20 transition-all duration-200"
    aria-label="Social link">
    <Icon size={15} />
  </a>
);

/* Animated gradient orb */
const Orb = ({ className }) => (
  <div className={`absolute rounded-full blur-3xl opacity-20 pointer-events-none ${className}`} />
);

const Footer = () => {
  const [email, setEmail] = useState("");

  const handleNewsletter = (e) => {
    e.preventDefault();
    if (!email) return;
    toast.success("You're in! Welcome to StoreOnline.");
    setEmail("");
  };

  return (
    <footer className="relative bg-dark-900 text-gray-300 overflow-hidden">
      {/* Decorative orbs */}
      <Orb className="w-96 h-96 bg-brand-600 -top-48 -left-24" />
      <Orb className="w-72 h-72 bg-accent-500 -bottom-24 right-0" />

      {/* Top wave SVG divider */}
      <div className="absolute top-0 left-0 right-0 overflow-hidden leading-none">
        <svg viewBox="0 0 1440 60" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full" preserveAspectRatio="none" style={{ height: 40 }}>
          <path d="M0,40 C360,0 1080,80 1440,20 L1440,0 L0,0 Z" fill="white"/>
        </svg>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">

          {/* Brand column */}
          <div className="lg:col-span-1">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2.5 mb-5">
              <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                <defs>
                  <linearGradient id="fLogoGrad" x1="0" y1="0" x2="32" y2="32" gradientUnits="userSpaceOnUse">
                    <stop offset="0%" stopColor="#7026f7"/>
                    <stop offset="100%" stopColor="#f59e0b"/>
                  </linearGradient>
                </defs>
                <rect width="32" height="32" rx="10" fill="url(#fLogoGrad)"/>
                <path d="M8 20L14 9l4 7 3-4 5 8H8z" fill="white" opacity="0.95"/>
                <circle cx="23" cy="11" r="2" fill="white" opacity="0.7"/>
              </svg>
              <span className="font-display font-bold text-xl text-white tracking-tight">
                Store<span className="gradient-text">Online</span>
              </span>
            </Link>

            <p className="text-sm text-gray-400 leading-relaxed mb-5">
              Premium compact electronics — drones, audio, wearables, cameras, and smart gadgets for the modern tech enthusiast.
            </p>

            {/* Company info */}
            <div className="space-y-2 text-xs text-gray-500 mb-6">
              <p className="text-gray-300 font-display font-semibold text-sm">TS ELITE SOURCE LLC</p>
              <div className="flex items-start gap-2">
                <FiMapPin size={12} className="mt-0.5 flex-shrink-0 text-brand-400" />
                <span>5752 Keron Ln, Grovetown GA 30813</span>
              </div>
              <div className="flex items-center gap-2">
                <FiMail size={12} className="flex-shrink-0 text-brand-400" />
                <a href="mailto:info@e-storeonline.online" className="hover:text-brand-400 transition-colors">
                  info@e-storeonline.online
                </a>
              </div>
            </div>

            <div className="flex gap-2">
              <SocialBtn Icon={FiInstagram} href="https://storeonline.online" />
              <SocialBtn Icon={FiTwitter}   href="https://storeonline.online" />
              <SocialBtn Icon={FiFacebook}  href="https://storeonline.online" />
              <SocialBtn Icon={FiYoutube}   href="https://storeonline.online" />
            </div>
          </div>

          {/* Shop links */}
          <div>
            <h4 className="font-display font-semibold text-white mb-5 text-sm uppercase tracking-widest">Shop</h4>
            <ul className="space-y-3 text-sm">
              {["Drones", "Audio", "Wearables", "Cameras", "Smart Gadgets"].map(cat => (
                <li key={cat}>
                  <Link to={`/shop?category=${cat}`}
                    className="text-gray-400 hover:text-brand-400 transition-colors flex items-center gap-1.5 group">
                    <FiArrowRight size={11} className="opacity-0 group-hover:opacity-100 transition-opacity -ml-3 group-hover:ml-0" />
                    {cat}
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/shop" className="text-gray-400 hover:text-brand-400 transition-colors flex items-center gap-1.5 group">
                  <FiArrowRight size={11} className="opacity-0 group-hover:opacity-100 transition-opacity -ml-3 group-hover:ml-0" />
                  All Products
                </Link>
              </li>
            </ul>
          </div>

          {/* Company links */}
          <div>
            <h4 className="font-display font-semibold text-white mb-5 text-sm uppercase tracking-widest">Company</h4>
            <ul className="space-y-3 text-sm">
              {[
                { label: "About Us",         to: "/" },
                { label: "Terms & Conditions", to: "/terms" },
                { label: "Privacy Policy",    to: "/privacy" },
                { label: "Cart",              to: "/cart" },
              ].map(({ label, to }) => (
                <li key={label}>
                  <Link to={to} className="text-gray-400 hover:text-brand-400 transition-colors flex items-center gap-1.5 group">
                    <FiArrowRight size={11} className="opacity-0 group-hover:opacity-100 transition-opacity -ml-3 group-hover:ml-0" />
                    {label}
                  </Link>
                </li>
              ))}
              <li>
                <a href="mailto:info@e-storeonline.online"
                  className="text-gray-400 hover:text-brand-400 transition-colors flex items-center gap-1.5 group">
                  <FiArrowRight size={11} className="opacity-0 group-hover:opacity-100 transition-opacity -ml-3 group-hover:ml-0" />
                  Contact Support
                </a>
              </li>
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h4 className="font-display font-semibold text-white mb-5 text-sm uppercase tracking-widest">Newsletter</h4>
            <p className="text-sm text-gray-400 mb-4 leading-relaxed">
              Get early access to new drops, flash sales, and exclusive tech guides.
            </p>
            <form onSubmit={handleNewsletter} className="space-y-2">
              <div className="flex gap-2">
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="flex-1 px-3 py-2.5 bg-white/5 border border-white/10 rounded-xl text-sm text-white placeholder-gray-500 focus:border-brand-500 focus:ring-1 focus:ring-brand-500/30 outline-none font-sans transition-all"
                  required
                />
                <button type="submit"
                  className="px-4 py-2.5 bg-brand-600 text-white rounded-xl text-sm font-medium hover:bg-brand-500 hover:shadow-glow transition-all duration-200">
                  <FiSend size={15} />
                </button>
              </div>
              <p className="text-xs text-gray-600">No spam, ever. Unsubscribe anytime.</p>
            </form>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="relative border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-gray-600 font-sans">
            © {new Date().getFullYear()} TS ELITE SOURCE LLC · storeonline.online · All rights reserved.
          </p>
          <div className="flex items-center gap-5 text-xs text-gray-600">
            {[["🔒","SSL Secured"],["📦","Fast Shipping"],["↩️","Easy Returns"]].map(([icon, label]) => (
              <span key={label} className="flex items-center gap-1">{icon} {label}</span>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
